import { memo, useRef, useEffect } from 'react';
import { FR_MONTHS } from './constants';

function formatRange(startDate, days) {
  const s = new Date(startDate.toString('yyyy-MM-ddTHH:mm:ss'));
  const e = new Date(s);
  e.setDate(e.getDate() + days - 1);
  const sDay = s.getDate();
  const eDay = e.getDate();
  const sMonth = FR_MONTHS[s.getMonth()].toLowerCase().slice(0, 4).replace('.', '');
  const eMonth = FR_MONTHS[e.getMonth()].toLowerCase().slice(0, 4).replace('.', '');
  const eYear = e.getFullYear();
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${sDay} ${sMonth} - ${eDay} ${eMonth} ${eYear}`;
  }
  return `${sDay} ${sMonth} - ${eDay} ${eMonth} ${eYear}`;
}

export const SchedulerToolbar = memo(function SchedulerToolbar({
  currentView,
  startDate,
  days,

  isDatePickerOpen,
  customStart,
  customEnd,
  periodStats,
  filterProject,
  filterType,
  projectFilterOptions,
  typeFilterOptions,
  onNavigate,
  onGoToday,
  onSwitchView,
  onToggleDatePicker,

  onCustomStartChange,
  onCustomEndChange,
  onApplyCustomRange,
  onFilterProjectChange,
  onFilterTypeChange,
}) {
  const calendarRef = useRef(null);

  useEffect(() => {
    if (!isDatePickerOpen) return;
    const handler = (e) => {
      if (calendarRef.current?.contains(e.target)) return;
      onToggleDatePicker();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isDatePickerOpen, onToggleDatePicker]);

  return (
    <div className="border-b border-gray-200 mb-3 sm:mb-4 bg-white rounded-lg shadow-sm p-2 sm:p-3 border border-gray-200 z-30 relative">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        {/* Navigation: <- period -> */}
        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1 relative">
          <button
            onClick={() => onNavigate(-1)}
            className="p-1 hover:bg-white rounded shadow-sm transition-all text-gray-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <span className="px-2 font-semibold text-gray-700 min-w-[180px] text-center select-none text-sm">
            {formatRange(startDate, days)}
          </span>
          <button
            onClick={() => onNavigate(1)}
            className="p-1 hover:bg-white rounded shadow-sm transition-all text-gray-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>

        {/* Aujourd'hui */}
        <button
          onClick={onGoToday}
          className="btn btn-secondary btn-sm"
        >
          Aujourd&apos;hui
        </button>

        {/* Sem. | Mois */}
        <div className="flex items-center bg-white border border-gray-300 rounded-md overflow-hidden">
          <button
            onClick={() => onSwitchView('week')}
            className={`text-xs px-2.5 py-1.5 border-r border-gray-300 transition-colors ${
              currentView === 'week'
                ? 'bg-iris-50 text-iris-700 font-medium'
                : 'text-gray-600 hover:bg-iris-50 hover:text-iris-600'
            }`}
          >
            Sem.
          </button>
          <button
            onClick={() => onSwitchView('month')}
            className={`text-xs px-2.5 py-1.5 transition-colors ${
              currentView === 'month'
                ? 'bg-iris-50 text-iris-700 font-medium'
                : 'text-gray-600 hover:bg-iris-50 hover:text-iris-600'
            }`}
          >
            Mois
          </button>
        </div>

        {/* Calendar popover */}
        <div ref={calendarRef} className="relative">
          <button
            onClick={onToggleDatePicker}
            className={`p-1.5 rounded-md border transition-colors ${
              isDatePickerOpen
                ? 'bg-iris-50 border-iris-400 text-iris-600'
                : 'bg-white border-gray-300 text-gray-500 hover:border-iris-400 hover:text-iris-600'
            }`}
            title="Période personnalisée"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
          </button>

          {isDatePickerOpen && (
            <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl z-50" style={{ width: '300px' }}>
              <div className="px-4 py-3">
                <div className="text-xs text-gray-500 font-medium mb-2">Période personnalisée</div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Du</label>
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => onCustomStartChange(e.target.value)}
                      className="input text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Au</label>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => onCustomEndChange(e.target.value)}
                      className="input text-xs"
                    />
                  </div>
                </div>
                <button
                  onClick={onApplyCustomRange}
                  className="btn btn-primary btn-sm btn-block"
                >
                  Appliquer la période
                </button>
                <div className="text-xs text-gray-400 text-center mt-1.5">Max. 1 an</div>
              </div>
            </div>
          )}
        </div>

        {/* Filters + stats pushed to the right */}
        <div className="flex flex-wrap items-center gap-1.5 ml-auto">
          {projectFilterOptions.length > 0 && (
            <select
              value={filterProject}
              onChange={(e) => onFilterProjectChange(e.target.value)}
              className="select text-xs min-w-0 w-auto"
            >
              <option value="">Tous les projets</option>
              {projectFilterOptions.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          {typeFilterOptions.length > 0 && (
            <select
              value={filterType}
              onChange={(e) => onFilterTypeChange(e.target.value)}
              className="select text-xs min-w-0 w-auto"
            >
              <option value="">Tous les types</option>
              {typeFilterOptions.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
          <span className="bg-blue-50 text-blue-700 px-1.5 sm:px-2 py-0.5 rounded-full font-medium whitespace-nowrap text-[10px] sm:text-xs">
            {periodStats.total} affect.
          </span>
          <span className="bg-green-50 text-green-700 px-1.5 sm:px-2 py-0.5 rounded-full font-medium whitespace-nowrap text-[10px] sm:text-xs">
            {periodStats.assigned}/{periodStats.resCount} collab.
          </span>
          <span className="bg-orange-50 text-orange-700 px-1.5 sm:px-2 py-0.5 rounded-full font-medium whitespace-nowrap text-[10px] sm:text-xs">
            ~{periodStats.avgPerRes}h/p.
          </span>
        </div>
      </div>

      <div className="text-[10px] sm:text-[11px] text-gray-400 italic leading-tight mt-1.5">
        Planning pr&eacute;visionnel &mdash; les affectations n&rsquo;impliquent pas de logs de temps sur les projets ou t&acirc;ches.
      </div>
    </div>
  );
});
